import React, { useEffect } from 'react';
import styled from '@2pulse/responsive';
import { useForm, useFieldArray } from 'react-hook-form';
import { normalize } from '@app/helpers';
import { 
  IProduct, 
  ICatalogSnapshotToShop, 
  ICatalogSnapshotToShopProductOverride,
  ICatalogSnapshotToShopProductVariationOverride,
  convertToEuros
} from '@app/common';
import { Button, Form, Modal } from '@app/components';
import { CatalogShopPriceInput } from './CatalogShopPriceInput';
import { catalogSnapshotToShopApi } from '@app/store';
import { useToast } from '@app/hooks';

export const CatalogShopProductEditModalId = 'catalog-shop-product-edit';

interface CatalogShopProductEditProps {
  isOpen: boolean;
  product: IProduct | null;
  catalogSnapshotToShop: ICatalogSnapshotToShop | undefined;
  onClose: () => void;
}

interface ProductOverrideFormValues {
  productId: string;
  productVariations: ICatalogSnapshotToShopProductVariationOverride[];
}

const transformToFormValues = (
  product: IProduct | null, 
  catalogSnapshotToShop: ICatalogSnapshotToShop | undefined
): ProductOverrideFormValues => {
  if (!product) {
    return {
      productId: '',
      productVariations: []
    };
  }

  const existingOverride = catalogSnapshotToShop?.data?.productsOverrides?.find(
    override => override.productId === product._id
  );

  return {
    productId: product._id,
    productVariations: product.productVariations.map(pv => {
      const variationOverride = existingOverride?.productVariations?.find(
        vo => vo.productVariationId === pv._id
      );

      return {
        productVariationId: pv._id,
        disabled: variationOverride?.disabled ?? null,
        price: variationOverride?.price ?? null,
        deliveryPrice: variationOverride?.deliveryPrice ?? null,
        takeAwayPrice: variationOverride?.takeAwayPrice ?? null,
        onSpotPrice: variationOverride?.onSpotPrice ?? null,
      };
    })
  };
};

export const CatalogShopProductEdit = ({ 
  isOpen, 
  product, 
  catalogSnapshotToShop, 
  onClose 
}: CatalogShopProductEditProps) => {
  const toast = useToast();
  const [updateCatalogSnapshotToShop, { isLoading, error: apiErrors }] = catalogSnapshotToShopApi.useUpdateCatalogSnapshotToShopMutation();

  const formMethods = useForm<ProductOverrideFormValues>({
    defaultValues: transformToFormValues(product, catalogSnapshotToShop)
  });

  const { fields } = useFieldArray({
    control: formMethods.control,
    name: 'productVariations'
  });

  useEffect(() => {
    if (isOpen && product) {
      formMethods.reset(transformToFormValues(product, catalogSnapshotToShop));
    }
  }, [isOpen, product, catalogSnapshotToShop]);

  const onSubmit = async (formValues: ProductOverrideFormValues) => {
    if (!catalogSnapshotToShop) return;

    try {
      const existingProductsOverrides = catalogSnapshotToShop.data?.productsOverrides?.filter(
        override => override.productId !== formValues.productId
      ) || [];

      // Keep the existing disabled state
      const existingOverride = catalogSnapshotToShop.data?.productsOverrides?.find(
        override => override.productId === formValues.productId
      );

      const newProductOverride: ICatalogSnapshotToShopProductOverride = {
        productId: formValues.productId,
        disabled: existingOverride?.disabled ?? null,
        productVariations: formValues.productVariations
      };

      await updateCatalogSnapshotToShop({
        _id: catalogSnapshotToShop._id,
        data: {
          ...catalogSnapshotToShop.data,
          productsOverrides: [...existingProductsOverrides, newProductOverride]
        }
      }).unwrap();

      toast.success('Prix mis à jour avec succès');
      onClose();
    } catch (error) {
      toast.error('Une erreur est survenue lors de la mise à jour');
    }
  };

  if (!isOpen || !product) return null;

  return (
    <Modal id={CatalogShopProductEditModalId} isOpen={isOpen}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{product.name}</ModalTitle>
          <ModalSubtitle>Modifier les prix par déclinaison</ModalSubtitle>
        </ModalHeader>

        <Form formMethods={formMethods} apiErrors={apiErrors}>
          <ModalContent>
            {fields.map((field, index) => {
              const productVariation = product.productVariations.find(
                pv => pv._id === field.productVariationId
              );

              if (!productVariation) return null;

              return (
                <VariationCard key={field.id}>
                  <VariationHeader>
                    <VariationName>
                      {productVariation.variation?.name || `Déclinaison ${index + 1}`}
                    </VariationName>
                  </VariationHeader>

                  <PriceGrid>
                    <PriceItem>
                      <PriceLabel>Prix de base</PriceLabel>
                      <PriceRow>
                        <CatalogPrice>
                          Catalogue: {productVariation.price ? `${convertToEuros(productVariation.price).toFixed(2)} €` : '-'}
                        </CatalogPrice>
                        <CatalogShopPriceInput
                          name={`productVariations.${index}.price`}
                          label="Prix shop"
                          catalogValue={productVariation.price}
                        />
                      </PriceRow>
                    </PriceItem>

                    <PriceItem>
                      <PriceLabel>Prix livraison</PriceLabel>
                      <PriceRow>
                        <CatalogPrice>
                          Catalogue: {productVariation.deliveryPrice ? `${convertToEuros(productVariation.deliveryPrice).toFixed(2)} €` : '-'}
                        </CatalogPrice>
                        <CatalogShopPriceInput
                          name={`productVariations.${index}.deliveryPrice`}
                          label="Prix shop"
                          catalogValue={productVariation.deliveryPrice}
                        />
                      </PriceRow>
                    </PriceItem>

                    <PriceItem>
                      <PriceLabel>Prix à emporter</PriceLabel>
                      <PriceRow>
                        <CatalogPrice>
                          Catalogue: {productVariation.takeAwayPrice ? `${convertToEuros(productVariation.takeAwayPrice).toFixed(2)} €` : '-'}
                        </CatalogPrice>
                        <CatalogShopPriceInput
                          name={`productVariations.${index}.takeAwayPrice`}
                          label="Prix shop"
                          catalogValue={productVariation.takeAwayPrice}
                        />
                      </PriceRow>
                    </PriceItem>

                    <PriceItem>
                      <PriceLabel>Prix sur place</PriceLabel>
                      <PriceRow>
                        <CatalogPrice>
                          Catalogue: {productVariation.onSpotPrice ? `${convertToEuros(productVariation.onSpotPrice).toFixed(2)} €` : '-'}
                        </CatalogPrice>
                        <CatalogShopPriceInput
                          name={`productVariations.${index}.onSpotPrice`}
                          label="Prix shop"
                          catalogValue={productVariation.onSpotPrice}
                        />
                      </PriceRow>
                    </PriceItem>
                  </PriceGrid>
                </VariationCard>
              );
            })}
          </ModalContent>

          <ModalActions>
            <Button
              title="Annuler"
              onPress={onClose}
              bordered
              grow
              large
            />
            <Button
              title="Enregistrer"
              onPress={formMethods.handleSubmit(onSubmit)}
              loading={isLoading}
              grow
              large
            />
          </ModalActions>
        </Form>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled.View({
  backgroundColor: 'rgba(2,24,43,0.98)',
  borderRadius: normalize(16),
  width: '90%',
  maxWidth: 700,
  maxHeight: '90%',
});

const ModalHeader = styled.View({
  padding: normalize(24),
  borderBottomWidth: 1,
  borderBottomColor: 'rgba(255,255,255,0.1)',
});

const ModalTitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(22),
  fontWeight: '700',
  color: theme.colors.babypowder,
  marginBottom: normalize(4),
}));

const ModalSubtitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.satanGray,
}));

const ModalContent = styled.ScrollView({
  padding: normalize(24),
  maxHeight: 450,
});

const VariationCard = styled.View({
  backgroundColor: 'rgba(255,255,255,0.05)',
  borderRadius: normalize(12),
  padding: normalize(16),
  marginBottom: normalize(16),
});

const VariationHeader = styled.View({
  marginBottom: normalize(16),
  paddingBottom: normalize(12),
  borderBottomWidth: 1,
  borderBottomColor: 'rgba(255,255,255,0.1)',
});

const VariationName = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '600',
  color: theme.colors.gold,
}));

const PriceGrid = styled.View({
  gap: normalize(16),
});

const PriceItem = styled.View({
  gap: normalize(8),
});

const PriceLabel = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(12),
  fontWeight: '600',
  color: theme.colors.babypowder,
  textTransform: 'uppercase',
  letterSpacing: 1,
}));

const PriceRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: normalize(16),
});

const CatalogPrice = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(13),
  color: 'rgba(255,255,255,0.5)',
}));

const ModalActions = styled.View({
  flexDirection: 'row',
  gap: normalize(16),
  padding: normalize(24),
  borderTopWidth: 1,
  borderTopColor: 'rgba(255,255,255,0.1)',
});
